<template>
    <div>
        <limitless-page-header title="Kundtoppen" />
        <div class="content">
            <div class="card">
                <div class="card-header"><h5 class="card-title">Urval</h5></div>
                <div class="card-body">
                    <form @submit.prevent="getReport">
                        <div class="row">
                            <div class="col-md-4 form-group">
                                <label for="customer-stats-year">{{ filters.func === 'old_customers' ? 'Årsgräns' : 'Säsong' }}</label>
                                <select id="customer-stats-year" v-model="filters.year" class="form-control" :disabled="loading">
                                    <option v-for="year in years" :key="year" :value="String(year)">{{ filters.func === 'old_customers' ? 'Sedan ' + year + '-08-31' : year + '-09-01 – ' + (year + 1) + '-08-31' }}</option>
                                    <option v-if="filters.func !== 'old_customers'" value="TUTTI">Alla år</option>
                                </select>
                            </div>
                            <div class="col-md-4 form-group">
                                <label for="customer-stats-mode">Rapport</label>
                                <select id="customer-stats-mode" v-model="filters.func" class="form-control" :disabled="loading" @change="modeChanged">
                                    <option value="">Topplistan</option>
                                    <option value="extendedinfo">Topplistan med extra kundinformation</option>
                                    <option value="old_customers">Tappade kunder</option>
                                </select>
                            </div>
                            <div class="col-md-4 form-group">
                                <label for="customer-stats-seller">Säljare</label>
                                <select id="customer-stats-seller" v-model="filters.uid" class="form-control" :disabled="loading">
                                    <option value="">Alla säljare</option>
                                    <option v-for="seller in sellers" :key="seller.value" :value="String(seller.value)">{{ seller.text }}</option>
                                </select>
                            </div>
                        </div>
                        <limitless-button type="submit" icon="icon-stats-bars" :busy="loading">Hämta statistik</limitless-button>
                    </form>
                </div>
            </div>

            <div v-if="error_message" class="alert alert-danger" role="alert">{{ error_message }}</div>
            <div v-if="loading" class="text-center mb-3" role="status"><i class="icon-spinner2 spinner mr-2"></i>Hämtar kundstatistik...</div>

            <rednax-datatable v-if="report && !loading" :data="report.rows" :columns="columns" sort-by="value" sort-order="desc">
                <template #header>
                    <div>
                        <h5 class="card-title">{{ report_title }}</h5>
                        <p v-if="report.filters.func === 'old_customers'" class="text-muted mb-0">
                            Kunder med köp före {{ report.filters.year }}-09-01 som inte har lagt någon giltig order sedan dess. Ordervärdet avser alla tidigare köp.
                        </p>
                        <p class="text-muted mb-0">Genererad: {{ report.generated_at }}. {{ report.totals.customers }} kunder.</p>
                    </div>
                </template>
                <template #cell_company_name="{ row, cell }">
                    <router-link :to="'/customers/' + row.customer_id">{{ cell || row.customer_id }}</router-link>
                </template>
                <template #cell_quantity="{ row, cell }">
                    <router-link :to="'/customers/' + row.customer_id">{{ formatNumber(cell, 0) }}</router-link>
                </template>
                <template #cell_value="{ row, cell }">
                    <router-link :to="'/customers/' + row.customer_id">{{ formatNumber(cell, 2) }} kr</router-link>
                </template>
                <template #cell_previous_value="{ row, cell }">
                    <router-link :to="'/customers/' + row.customer_id">{{ formatNumber(cell, 2) }} kr</router-link>
                </template>
                <template #cell_latest_order="{ cell }">{{ cell ? cell.substring(0, 10) : 'Saknas' }}</template>
                <template #cell_seller_name="{ row, cell }">
                    <router-link v-if="row.seller_id" :to="sellerLink(row.seller_id)">{{ cell || row.seller_id }}</router-link>
                    <span v-else>Saknas</span>
                </template>
                <template #tfoot>
                    <tr>
                        <th v-for="column in columns" :key="column.data" :class="{ 'text-right': column.align_right }">
                            <span v-if="column.data === 'company_name'">Totalt</span>
                            <span v-else-if="column.data === 'quantity'">{{ formatNumber(report.totals.quantity, 0) }}</span>
                            <span v-else-if="column.data === 'value'">{{ formatNumber(report.totals.value, 2) }} kr</span>
                            <span v-else-if="column.data === 'previous_value'">{{ formatNumber(report.totals.previous_value, 2) }} kr</span>
                        </th>
                    </tr>
                </template>
            </rednax-datatable>
        </div>
    </div>
</template>

<script>
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import RednaxDatatable from '@components/Datatable/RednaxDatatableComponent';
import apiClient from '@services/ApiClient';
import { formatNumber } from '@services/Helpers';

function currentSeason() {
    const today = new Date();
    return today.getMonth() >= 8 ? today.getFullYear() : today.getFullYear() - 1;
}

function defaultFilters() {
    return { year: String(currentSeason()), uid: '', func: '' };
}

export default {
    name: 'CustomerStats',
    components: { LimitlessPageHeader, LimitlessButton, RednaxDatatable },
    data() {
        return {
            filters: defaultFilters(),
            years: Array.from({ length: currentSeason() - 2004 }, (_, index) => currentSeason() - index),
            sellers: [],
            report: null,
            loading: false,
            refresh_pending: false,
            page_active: true,
            error_message: '',
        };
    },
    mounted() {
        this.readQuery();
    },
    beforeDestroy() {
        this.page_active = false;
    },
    watch: {
        '$route.query': function queryChanged(query) {
            if (!this.report || !this.queryMatches(query, this.report.filters)) {
                this.readQuery();
            }
        },
    },
    computed: {
        report_title() {
            const { year, func } = this.report.filters;
            if (func === 'old_customers') return 'Tappade kunder sedan ' + year + '-08-31';
            if (year === 'TUTTI') return 'Kundtoppen – alla år';
            return 'Kundtoppen ' + year + '/' + (Number(year) + 1);
        },
        columns() {
            const { year, func } = this.report.filters;
            const columns = [
                { data: 'company_name', header: 'Kund' },
                { data: 'customer_group', header: 'Kundgrupp' },
                { data: 'quantity', header: 'Antal kort', align_right: true },
                { data: 'value', header: 'Ordervärde', align_right: true },
                { data: 'latest_order', header: 'Senaste order' },
                { data: 'seller_name', header: 'Säljare' },
            ];
            if (year !== 'TUTTI') {
                columns.push({
                    data: 'previous_value',
                    header: func === 'old_customers' ? 'Tappade sedan ' + (Number(year) - 1) + '-08-31' : 'Ordervärde ' + (Number(year) - 1) + '/' + year,
                    align_right: true,
                });
            }
            if (['extendedinfo', 'old_customers'].includes(func)) {
                columns.push(
                    { data: 'customer_id', header: 'Kund-ID', align_right: true },
                    { data: 'ContactName', header: 'Kontaktperson' },
                    { data: 'ContactPhone', header: 'Telefon' },
                    { data: 'ContactEmail', header: 'E-post' },
                    { data: 'InvoiceAddress', header: 'Fakturaadress' },
                    { data: 'InvoiceZip', header: 'Fakturapostnummer' },
                    { data: 'InvoiceCity', header: 'Fakturaort' },
                    { data: 'DeliveryContactName', header: 'Leveranskontakt' },
                    { data: 'DeliveryAddress', header: 'Leveransadress' },
                    { data: 'DeliveryZip', header: 'Leveranspostnummer' },
                    { data: 'DeliveryCity', header: 'Leveransort' },
                );
            }
            return columns.map((column) => ({ ...column, sortable: true }));
        },
    },
    methods: {
        formatNumber,
        queryMatches(query, filters) {
            return Object.keys(filters).every((key) => (query[key] ?? '') === filters[key]);
        },
        modeChanged() {
            if (this.filters.func === 'old_customers' && this.filters.year === 'TUTTI') {
                this.filters.year = String(currentSeason());
            }
        },
        readQuery() {
            if (this.loading) {
                this.refresh_pending = true;
                return;
            }
            this.filters = defaultFilters();
            Object.keys(this.filters).forEach((key) => {
                if (typeof this.$route.query[key] === 'string') {
                    this.filters[key] = this.$route.query[key];
                }
            });
            this.getReport();
        },
        sellerLink(id) {
            return { name: 'stats.customers', query: { ...this.report.filters, uid: String(id) } };
        },
        async getReport() {
            if (this.loading) return;
            this.loading = true;
            this.error_message = '';
            try {
                const response = await apiClient.get('/vue/stats/customers', { params: { ...this.filters } });
                if (!this.page_active || this.refresh_pending) return;
                this.report = response.data;
                this.filters = { ...response.data.filters };
                this.years = response.data.years;
                this.sellers = response.data.sellers;
                if (!this.queryMatches(this.$route.query, this.filters)) {
                    const query = Object.fromEntries(Object.entries(this.filters).filter(([, value]) => value !== ''));
                    await this.$router.replace({ name: 'stats.customers', query });
                }
            } catch (error) {
                this.report = null;
                this.error_message = error.response?.status === 422 && error.response.data?.errors
                    ? Object.values(error.response.data.errors).flat().join(' ')
                    : 'Kundstatistiken kunde inte hämtas. Försök igen.';
            } finally {
                this.loading = false;
                if (this.page_active && this.refresh_pending) {
                    this.refresh_pending = false;
                    this.readQuery();
                }
            }
        },
    },
};
</script>
